import HBMessage from "@/components/HBMessage";
import { GDTrackerInitPromise } from "@/tracker";
import gdABTest from "@gaoding/gd-abtest";
import { isCancel } from "axios";
import dayjs from "dayjs";
import { encode } from "js-base64";
import { match } from "path-to-regexp";
import { Cache } from "./cache";
import createRequest from "./createRequest";
import { getHttpAgent } from "./getHttpAgent";

// yapi、dev、fat、stage、production
const API = {
  yapi: "https://yapi.gaoding.com/mock/1536",
  dev: "https://api-dev.huaban.com",
  fat: "https://api-fat.huaban.com",
  stage: "https://api-stage.huaban.com",
  prod: "https://api.huaban.com",
};

export const env = process.env.NEXT_PUBLIC_DEPLOY_ENV || "prod";

export const BaseURL = API[env];
export const REQUEST_MESSAGE_KEY = "REQUEST_MESSAGE_KEY";

/**
 * @type {Cache}
 */
let requestCache;

/**
 * 获取需要添加的abtest实验标识头信息
 * @param {*} url
 * @returns
 */
async function getABTestHeader(url) {
  return new Promise(async (resolve) => {
    try {
      // 1秒超时，则继续执行，不阻塞请求
      setTimeout(() => {
        resolve({});
      }, 1000);

      // 等待abtest初始化完成
      if (GDTrackerInitPromise) {
        await GDTrackerInitPromise;
      }

      if (!gdABTest.option || typeof window === "undefined") {
        // gdABTest未初始化；
        return resolve({});
      }

      const exps = await gdABTest.getExps();

      if (!exps) return {};

      const xAbtestTags = {};
      var hasMatch = false;
      exps.forEach((exp) => {
        if (exp.experimentUrls.find((path) => match(path)(url))) {
          xAbtestTags[exp.variableName] = exp.variableValue;
          hasMatch = true;
        }
      });

      // 有匹配的实验需要添加标识
      if (hasMatch) {
        const { trackerWind } = await import("@gaoding/gd-tracker");

        const header = {
          "guest-token": trackerWind.get("common", "device_id").device_id,
          "x-guest-token": trackerWind.get("common", "device_id").device_id,
          "x-abtest-tags": encode(JSON.stringify(xAbtestTags)),
        };

        resolve(header);
      } else {
        resolve({});
      }
    } catch (error) {
      console.error("请求获取abtest标识出错", error);
      resolve({});
    }
  });
}

const axios = createRequest({
  BaseURL,
});

/**
 * 公共请求方法
 * @param {{
 *  url: string,
 *  params?: Object,
 *  query?: Object,
 *  ignoreMessage?: boolean,
 *  ignoreError?: boolean,
 *  serverSideHttps?: boolean,
 *  cache?: boolean | {time: number}
 * } & import('axios').AxiosRequestConfig} param
 * @returns
 */
const request = async ({
  url = "/",
  params, // url匹配参数
  query, // 查询参数
  headers = {},
  ignoreMessage,
  ignoreError,
  ignoreCancel,
  cache,
  serverSideHttps,
  ...opts
}) => {
  try {
    const abtestHeader = await getABTestHeader(url);
    let cacheKey;
    if (cache) {
      if (!requestCache) {
        requestCache = new Cache();
      }

      cacheKey = requestCache.generateKey({
        url,
        params,
        query,
        headers,
        ...opts,
      });

      const cacheData = requestCache.get(cacheKey);

      if (cacheData) {
        return cacheData;
      }
    }

    const res = await axios({
      url,
      params: query,
      options: {
        params,
        ignoreMessage,
        ignoreError,
        ignoreCancel,
      },
      timeout: 10000,
      headers: { ...headers, ...abtestHeader },
      // 服务端内网请求走http
      ...(typeof window === "undefined"
        ? {
            ...(!serverSideHttps
              ? { baseURL: BaseURL.replace("https", "http") }
              : null),
            httpAgent: getHttpAgent(),
          }
        : null),
      ...opts,
    });

    if (cache) {
      requestCache.set(cacheKey, res, {
        expireTime: cache?.time || 60000,
      });
    }

    return res;
  } catch (error) {
    const { response } = error;
    if (
      !ignoreError &&
      typeof window !== "undefined" &&
      (!isCancel(error) || !ignoreCancel)
    ) {
      let errmsg = error.toString();
      if (response?.data && response?.data?.error) {
        errmsg = response.data?.error;
      }
      // 处理各种错误形式
      if (typeof response?.data === "string") {
        errmsg = response.data;
      }

      HBMessage.error({
        content: errmsg,
        key: REQUEST_MESSAGE_KEY,
        duration: 3,
      });
      // 处理请求错误信息，方便具体查看是哪个接口
      error.message = `${url}: ${errmsg}`;
      throw error;
    }

    // 减少服务端日志（服务端会打印很多无用信息，影响排查问题）
    if (typeof window !== "undefined") {
      console.error("error", error);
    } else {
      console.warn(
        `${dayjs().format("YYYY/MM/DD HH:mm:ss")}`,
        url,
        " request error: ",
        error.message
      );
    }

    if (response?.data) {
      return response.data;
    }

    return error;
  }
};

export default request;
